import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { apiRequest } from '../utils/api';

interface ServiceAction {
  id: number;
  serviceId: number;
  actionType: 'cancel' | 'refill';
  provider: string;
  subtype: string | null;
  refillDays: number | null;
  isActive: boolean;
  createdAt: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

const PROVIDERS = ['sabo', 'new97_v2', 'mfb', 'like88', 'amazingcpanel', 'vietnamfb', 'tlc', 'buffviewer_v2', 'minsocial', 'traodoisub', 'tuongtaccheo'];

const ServiceActions: React.FC = () => {
  const [actions, setActions] = useState<ServiceAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAction, setEditingAction] = useState<ServiceAction | null>(null);
  const [form, setForm] = useState({ serviceId: '', actionType: 'cancel', provider: 'sabo', subtype: '', refillDays: '30', isActive: true });
  const [customSubtype, setCustomSubtype] = useState(false);
  const [filter, setFilter] = useState({ actionType: '', provider: '', serviceId: '' });
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [pagination, setPagination] = useState<Pagination>({ page: 1, limit: 50, total: 0, pages: 0 });

  useEffect(() => {
    const fetchActions = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        params.append('page', pagination.page.toString());
        params.append('limit', pagination.limit.toString());
        if (filter.actionType) params.append('actionType', filter.actionType);
        if (filter.provider) params.append('provider', filter.provider);
        if (filter.serviceId) params.append('serviceId', filter.serviceId);

        const resp = await apiRequest(`/api/service-actions?${params.toString()}`);
        if (resp.ok) {
          const data = await resp.json();
          setActions(data.data || []);
          if (data.pagination) {
            setPagination(prev => ({ ...prev, ...data.pagination }));
          }
        }
      } catch (error) {
        console.error('Fetch error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchActions();
  }, [filter, reloadTrigger, pagination.page, pagination.limit]);

  const handleToggleActive = async (action: ServiceAction) => {
    try {
      const resp = await apiRequest(`/api/service-actions/${action.id}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: !action.isActive })
      });
      if (resp.ok) {
        setReloadTrigger(r => r + 1);
      } else {
        const err = await resp.json();
        alert(err.error || 'Failed to toggle');
      }
    } catch (error) {
      alert('Failed to toggle');
    }
  };

  const handleSubmit = async () => {
    try {
      // For create: send serviceId as string to support comma-separated values
      // For update: send as number (single value only)
      const payload: Record<string, unknown> = {
        serviceId: editingAction ? parseInt(form.serviceId) : form.serviceId.trim(),
        actionType: form.actionType,
        provider: form.provider,
        subtype: form.subtype || null,
        refillDays: form.actionType === 'refill' ? parseInt(form.refillDays) : null,
        isActive: form.isActive
      };

      const url = editingAction ? `/api/service-actions/${editingAction.id}` : '/api/service-actions';
      const method = editingAction ? 'PUT' : 'POST';

      const resp = await apiRequest(url, { method, body: JSON.stringify(payload) });
      if (resp.ok) {
        const result = await resp.json();
        setShowModal(false);
        setEditingAction(null);
        setForm({ serviceId: '', actionType: 'cancel', provider: 'sabo', subtype: '', refillDays: '30', isActive: true });
        setCustomSubtype(false);
        setReloadTrigger(r => r + 1);
        // Show success message for bulk create
        if (result.created && result.created > 1) {
          alert(result.message);
        }
      } else {
        const err = await resp.json();
        alert(err.error || 'Failed to save');
      }
    } catch (error) {
      alert('Failed to save');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Xác nhận xóa cấu hình này?')) return;
    try {
      const resp = await apiRequest(`/api/service-actions/${id}`, { method: 'DELETE' });
      if (resp.ok) setReloadTrigger(r => r + 1);
    } catch (error) {
      alert('Failed to delete');
    }
  };

  const SUBTYPES = ['instagram', 'tiktok', 'facebook', 'threads', 'shares', 'views', 'fb-stories', 'fb-story-reaction', 'fb-comment', 'fb-saves', 'facebook-views', 'progress-cancel'];

  const handleEdit = (action: ServiceAction) => {
    setEditingAction(action);
    const subtypeValue = action.subtype || '';
    const isCustom = subtypeValue !== '' && !SUBTYPES.includes(subtypeValue);
    setCustomSubtype(isCustom);
    setForm({
      serviceId: action.serviceId.toString(),
      actionType: action.actionType,
      provider: action.provider,
      subtype: subtypeValue,
      refillDays: action.refillDays?.toString() || '30',
      isActive: action.isActive
    });
    setShowModal(true);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  return (
    <Layout currentPage="service-actions">
      <div style={{ padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h1 style={{ margin: 0 }}>Service Actions Configuration</h1>
          <button
            onClick={() => { setEditingAction(null); setForm({ serviceId: '', actionType: 'cancel', provider: 'sabo', subtype: '', refillDays: '30', isActive: true }); setCustomSubtype(false); setShowModal(true); }}
            style={{ padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
          >
            + Thêm mới
          </button>
        </div>

        <p style={{ color: '#6b7280', marginBottom: '20px' }}>
          Cấu hình dịch vụ hỗ trợ hủy (cancel) và bảo hành (refill). Thay thế file refill_services.txt và hardcoded service IDs.
        </p>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
          <input
            type="text"
            value={filter.serviceId}
            onChange={e => setFilter({ ...filter, serviceId: e.target.value })}
            placeholder="Tìm theo Service ID"
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', width: '150px' }}
          />
          <select value={filter.actionType} onChange={e => setFilter({ ...filter, actionType: e.target.value })} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
            <option value="">Tất cả loại</option>
            <option value="cancel">Cancel</option>
            <option value="refill">Refill</option>
          </select>
          <select value={filter.provider} onChange={e => setFilter({ ...filter, provider: e.target.value })} style={{ padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
            <option value="">Tất cả provider</option>
            {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        {/* Stats */}
        <div style={{ marginBottom: '10px', color: '#6b7280', fontSize: '14px' }}>
          Tổng: {pagination.total} cấu hình
        </div>

        {/* Table */}
        {loading ? <p>Loading...</p> : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Service ID</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Loại</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Provider</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Subtype</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Refill Days</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Active</th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {actions.map(action => (
                  <tr key={action.id} style={{ backgroundColor: action.isActive ? 'transparent' : '#f9fafb', opacity: action.isActive ? 1 : 0.6 }}>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>{action.serviceId}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                      <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', backgroundColor: action.actionType === 'cancel' ? '#fee2e2' : '#dcfce7', color: action.actionType === 'cancel' ? '#dc2626' : '#16a34a' }}>
                        {action.actionType}
                      </span>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>{action.provider}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                      {action.subtype ? (
                        <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', backgroundColor: '#e0e7ff', color: '#4338ca' }}>
                          {action.subtype}
                        </span>
                      ) : '-'}
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>{action.refillDays || '-'}</td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                      <button
                        onClick={() => handleToggleActive(action)}
                        style={{
                          padding: '4px 12px',
                          borderRadius: '4px',
                          border: 'none',
                          cursor: 'pointer',
                          backgroundColor: action.isActive ? '#dcfce7' : '#fee2e2',
                          color: action.isActive ? '#16a34a' : '#dc2626'
                        }}
                      >
                        {action.isActive ? 'Bật' : 'Tắt'}
                      </button>
                    </td>
                    <td style={{ padding: '12px', borderBottom: '1px solid #e5e7eb' }}>
                      <button onClick={() => handleEdit(action)} style={{ marginRight: '8px', padding: '4px 8px', cursor: 'pointer' }}>Edit</button>
                      <button onClick={() => handleDelete(action.id)} style={{ padding: '4px 8px', cursor: 'pointer', color: '#dc2626' }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', marginTop: '20px' }}>
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.page === 1}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: pagination.page === 1 ? 'not-allowed' : 'pointer', opacity: pagination.page === 1 ? 0.5 : 1 }}
                >
                  «
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: pagination.page === 1 ? 'not-allowed' : 'pointer', opacity: pagination.page === 1 ? 0.5 : 1 }}
                >
                  ‹
                </button>
                <span style={{ padding: '8px 16px' }}>
                  Trang {pagination.page} / {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer', opacity: pagination.page === pagination.pages ? 0.5 : 1 }}
                >
                  ›
                </button>
                <button
                  onClick={() => handlePageChange(pagination.pages)}
                  disabled={pagination.page === pagination.pages}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: pagination.page === pagination.pages ? 'not-allowed' : 'pointer', opacity: pagination.page === pagination.pages ? 0.5 : 1 }}
                >
                  »
                </button>
              </div>
            )}
          </>
        )}

        {/* Modal */}
        {showModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '8px', width: '400px' }}>
              <h2 style={{ marginTop: 0 }}>{editingAction ? 'Sửa cấu hình' : 'Thêm cấu hình mới'}</h2>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Service ID</label>
                <input
                  type="text"
                  value={form.serviceId}
                  onChange={e => setForm({ ...form, serviceId: e.target.value })}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  placeholder={editingAction ? "VD: 103" : "VD: 103 hoặc 103, 104, 105"}
                  disabled={!!editingAction}
                />
                {!editingAction && (
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>Nhập nhiều ID cách nhau bằng dấu phẩy để tạo hàng loạt</small>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Loại action</label>
                <select value={form.actionType} onChange={e => setForm({ ...form, actionType: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                  <option value="cancel">Cancel (Hủy đơn)</option>
                  <option value="refill">Refill (Bảo hành)</option>
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Provider</label>
                <select value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                  {PROVIDERS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              {form.actionType === 'cancel' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Subtype (API endpoint)</label>
                  {!customSubtype ? (
                    <select value={form.subtype} onChange={e => setForm({ ...form, subtype: e.target.value })} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}>
                      <option value="">-- Chọn subtype --</option>
                      <option value="instagram">Instagram</option>
                      <option value="tiktok">TikTok</option>
                      <option value="facebook">Facebook</option>
                      <option value="threads">Threads</option>
                      <option value="shares">Shares</option>
                      <option value="views">Views</option>
                      <option value="fb-stories">FB Stories</option>
                      <option value="fb-story-reaction">FB Story Reaction</option>
                      <option value="fb-comment">FB Comment</option>
                      <option value="fb-saves">FB Saves</option>
                      <option value="facebook-views">Facebook Views</option>
                      <option value="progress-cancel">Progress Cancel (1532, 1533)</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={form.subtype}
                      onChange={e => setForm({ ...form, subtype: e.target.value })}
                      style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                      placeholder="Nhập subtype tùy chỉnh, ví dụ: custom-endpoint"
                    />
                  )}
                  <div style={{ marginTop: '6px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#6b7280' }}>
                      <input
                        type="checkbox"
                        checked={customSubtype}
                        onChange={e => { setCustomSubtype(e.target.checked); if (!e.target.checked) setForm({ ...form, subtype: '' }); }}
                      />
                      Nhập thủ công
                    </label>
                  </div>
                  <small style={{ color: '#6b7280', fontSize: '12px' }}>Chỉ dùng cho MFB, Buffviewer. Xác định endpoint API cancel.</small>
                </div>
              )}

              {form.actionType === 'refill' && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Số ngày bảo hành</label>
                  <input
                    type="number"
                    value={form.refillDays}
                    onChange={e => setForm({ ...form, refillDays: e.target.value })}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  />
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} />
                  Kích hoạt
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowModal(false)} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer' }}>Hủy</button>
                <button onClick={handleSubmit} style={{ padding: '8px 16px', borderRadius: '6px', backgroundColor: '#2563eb', color: 'white', border: 'none', cursor: 'pointer' }}>Lưu</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ServiceActions;

